/* Copyright (c) 2024 MOCA Systems, Inc.  All rights reserved. */
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ComponentStore } from '@ngrx/component-store';
import { Injectable } from '@angular/core';
import { subjectSubscribeWithoutComplete } from 'src/app/plain-javascript/services/rxjs-internal-extensions';

/*
 * An interface that represents an observable that has been registered with the TPStateManager.
 * The subscription property is used to store the subscription to the external observable. At the beginning, it is undefined, because the observable has not been subscribed yet.
 * The subject$ property is used to store the subject that will emit the values of the observable.
 */
interface RegisteredObservable<T> {
	subscription?: Subscription;
	subject$: BehaviorSubject<T>;
}

/**
 * TPStateManager is a class that extends ComponentStore and provides a way to manage the state of external observables.
 * The main purpose is to be able to preserve the original lifecycle of the observables, as well as to provide an easy way to attach them to the current state lifecycle.
 * This means that when the service is destroyed, all the observables are automatically unsubscribed and the inner subjects are completed.
 * This also solves the problem of ComponentStore not being able to "keep the liveness" of external observables when mapping them to plain attributes in a "normal" ComponentStore state.
 *
 * @param State The type of the state object that will be managed by the ComponentStore.
 * @param ObservablesState The type of the emitted values of observables that will be managed by the TPStateManager.
 * @example
 * ```ts
 * export interface MyState {
 *  myValue: string;
 * }
 *
 * export interface MyObservablesState {
 *  myObservable: number;
 * }
 *
 * Injectable()
 * export class MyStateManager extends TPStateManager<MyState, MyObservablesState> {
 *  constructor() {
 *    super({ myValue: '' }, { myObservable: 0 });
 *  }
 * }
 * ```
 */
@Injectable()
export abstract class TPStateManager<
	State extends object,
	ObservablesState extends object,
	KeyOf = keyof ObservablesState,
	ValueOf = ObservablesState[keyof ObservablesState]
> extends ComponentStore<State> {
	private registeredLiveObservables = new Map<KeyOf, RegisteredObservable<ValueOf>>();

	constructor(initialState: State, initialSubjectValues?: ObservablesState) {
		super(initialState);
		if (initialSubjectValues) {
			this.initializeObservables(initialSubjectValues);
		}
	}

	private initializeObservables(initialSubjectValues: ObservablesState): void {
		Object.entries(initialSubjectValues).forEach(([key, defaultValue]) => {
			this.registerLiveObservable(key as KeyOf, defaultValue);
		});
	}

	private registerLiveObservable(key: KeyOf, defaultValue: ValueOf): void {
		const existing = this.registeredLiveObservables.get(key);

		if (existing) {
			throw new Error(`Observable with key "${String(key)}" is already registered.`);
		}

		const subject = new BehaviorSubject<ValueOf>(defaultValue);
		this.registeredLiveObservables.set(key, { subject$: subject });
	}

	private updateObservable(key: KeyOf, value: Observable<ValueOf>): void {
		const registeredObservable = this.registeredLiveObservables.get(key);

		if (!registeredObservable) {
			throw new Error(`Observable with key "${String(key)}" is not registered.`);
		}

		if (registeredObservable.subscription) {
			registeredObservable.subscription.unsubscribe();
		}

		registeredObservable.subscription = value
			.pipe(takeUntil(this.destroy$))
			.subscribe(subjectSubscribeWithoutComplete(registeredObservable.subject$));
	}

	/**
	 * Returns a function that, when called, updates an observable value associated with a given key.
	 *
	 * @param key - The key associated with the observable value to be updated. Must be a key of the ObservablesState type.
	 * @returns A function that takes an observable value and updates the observable associated with the provided key.
	 * @example
	 * ```ts
	 * const updater = this.liveUpdater('key');
	 * updater(observableValue);
	 * ```
	 */
	protected liveUpdater(key: KeyOf): (value: Observable<ValueOf>) => void {
		return (value: Observable<ValueOf>) => {
			this.updateObservable(key, value);
		};
	}

	/**
	 * Selects an observable associated with the given key.
	 *
	 * @template T - The type of the value emitted by the observable.
	 * @param key - The key associated with the observable to select. Must be a key of the ObservablesState type.
	 * @returns - The observable associated with the given key.
	 * @throws {Error} - Throws an error if the observable with the specified key is not registered. Register the observable in the constructor.
	 */
	protected selectLive$<T extends ValueOf>(key: KeyOf): Observable<T> {
		const registeredObservable = this.registeredLiveObservables.get(key);

		if (!registeredObservable) {
			throw new Error(`Observable with key "${String(key)}" is not registered.`);
		}

		return registeredObservable.subject$.asObservable() as Observable<T>;
	}

	/**
	 * Retrieves the current value (last emitted value) of the observable associated with the given key.
	 *
	 * @template T - The type of the value stored in the observable.
	 * @param key - The key associated with the registered observable. Must be a key of the ObservablesState type.
	 * @returns - The current value (last emitted value) of the observable associated with the given key.
	 * @throws {Error} - Throws an error if the observable with the specified key is not registered. Register the observable in the constructor.
	 */
	protected getSyncValue<T extends ValueOf>(key: KeyOf): T {
		const registeredObservable = this.registeredLiveObservables.get(key);

		if (!registeredObservable) {
			throw new Error(`Observable with key "${String(key)}" is not registered.`);
		}

		return registeredObservable.subject$.getValue() as T;
	}

	override ngOnDestroy(): void {
		this.registeredLiveObservables.forEach((reg) => {
			reg.subject$.complete();
		});
		this.registeredLiveObservables.clear();
		super.ngOnDestroy();
	}
}
